/*
 * moeka-recently-viewed.js  (v4 conversion UX — PDP)
 * Self-contained recently-viewed rail. Own localStorage key so it never
 * clashes with Horizon's RecentlyViewed helper. No Horizon imports.
 *
 *  1. Records the current product handle (most-recent-first, de-duped).
 *  2. Renders up to `data-max` previously viewed products, current excluded.
 *  3. Fetches product data from /products/{handle}.js.
 *  4. Reveals itself only once at least one card is rendered.
 *  5. Formats price using the shop money_format (handles {{amount}} variants).
 */
(function () {
  if (window.customElements && customElements.get('moeka-recently-viewed')) return;

  var STORAGE_KEY = 'moeka:recentlyViewed';
  var STORE_LIMIT = 12; // store a few more than we show

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / unavailable — fail silently */
    }
  }

  function record(handle) {
    if (!handle) return readList();
    var list = readList().filter(function (h) {
      return h !== handle;
    });
    list.unshift(handle);
    list = list.slice(0, STORE_LIMIT);
    writeList(list);
    return list;
  }

  // Minimal money formatter supporting Shopify money_format placeholders.
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') return '';
    var value = (cents / 100).toFixed(2);
    var fmt = format || '${{amount}}';

    function withCommas(num) {
      var parts = num.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount':
          return withCommas(value);
        case 'amount_no_decimals':
          return withCommas((cents / 100).toFixed(0));
        case 'amount_with_comma_separator':
          return value.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        case 'amount_no_decimals_with_comma_separator':
          return (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default:
          return withCommas(value);
      }
    });
  }

  class MoekaRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.currentHandle = this.getAttribute('data-current-handle') || '';
      this.max = parseInt(this.getAttribute('data-max') || '8', 10) || 8;
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.grid = this.querySelector('[data-moeka-recently-grid]');
      this.template = this.querySelector('[data-moeka-recently-template]');

      // Record current view first, then render the rest.
      var list = record(this.currentHandle);
      var handles = list
        .filter(
          function (h) {
            return h && h !== this.currentHandle;
          }.bind(this)
        )
        .slice(0, this.max);

      if (!handles.length || !this.grid || !this.template) return;
      this._render(handles);
    }

    _render(handles) {
      var self = this;
      var rendered = 0;

      var fetches = handles.map(function (handle) {
        return fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      });

      Promise.all(fetches).then(function (products) {
        // Preserve recency order.
        products.forEach(function (product) {
          if (!product || !product.handle) return;
          var card = self._buildCard(product);
          if (card) {
            self.grid.appendChild(card);
            rendered++;
          }
        });

        if (rendered > 0) {
          self.hidden = false;
        }
      });
    }

    _buildCard(product) {
      var frag = this.template.content.cloneNode(true);
      var anchor = frag.querySelector('a');
      var img = frag.querySelector('.moeka-recently__img');
      var titleEl = frag.querySelector('[data-field="title"]');
      var priceEl = frag.querySelector('[data-field="price"]');
      var pending = frag.querySelector('[data-moeka-photo-pending]');

      if (anchor) {
        anchor.href = product.url || '/products/' + product.handle;
        anchor.setAttribute('aria-label', product.title || '');
      }

      if (img) {
        var src = product.featured_image || (product.images && product.images[0]) || '';
        if (src) {
          // Request a reasonable size from Shopify's CDN.
          img.src = this._sized(src, 600);
          img.alt = product.title || '';
          if (pending) pending.remove();
        } else if (img.parentNode) {
          img.remove();
        }
      }

      if (titleEl) titleEl.textContent = product.title || '';

      if (priceEl) {
        var html = '';
        if (product.compare_at_price && product.compare_at_price > product.price) {
          html +=
            '<s style="opacity:0.5;font-weight:400;font-size:0.85em;">' +
            formatMoney(product.compare_at_price, this.moneyFormat) +
            '</s>&nbsp;';
        }
        html += formatMoney(product.price, this.moneyFormat);
        priceEl.innerHTML = html;
      }

      return frag;
    }

    _sized(url, width) {
      // Insert/replace a width param on Shopify CDN image URLs.
      try {
        var u = new URL(url, window.location.origin);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch (e) {
        return url;
      }
    }
  }

  customElements.define('moeka-recently-viewed', MoekaRecentlyViewed);
})();
